import axios from 'axios';
import { API_BASE_URL } from '../utils/constants';
import { getAuthToken } from '../utils/storage';
import { extractApiError } from '../utils/apiError';

/**
 * AI Advisor service.
 *
 * Wraps the /ai-advisor endpoints: chat-style threads (sessions), multi-turn
 * chat, and the purchase plan. The feature is ADMIN / SUPER_ADMIN only and is
 * gated per-tenant on the backend (returns 503 when disabled).
 *
 * Every method resolves to a uniform shape:
 *   { success: true, data }   on 2xx
 *   { success: false, error, status }   on failure
 * `status` is the HTTP status code (or null) so callers can special-case
 * 503 (not enabled) and 502 (AI service busy).
 */
const aiAdvisorAPI = axios.create({
  baseURL: `${API_BASE_URL}/api/v1`,
  headers: { 'Content-Type': 'application/json' },
});

// Attach auth token
aiAdvisorAPI.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

// Redirect on auth failure (mirrors the other services)
aiAdvisorAPI.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('user_data');
      sessionStorage.removeItem('auth_token');
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('user_data');
      window.location.href = '/login';
    }
    return Promise.reject(err);
  }
);

function fail(error) {
  return {
    success: false,
    error: extractApiError(error),
    status: error?.response?.status ?? null,
  };
}

const aiAdvisorService = {
  // ---- Scope picker ----

  /**
   * List Sub Categories (product catalogues) to populate the scope picker.
   * The chosen ids go into `catalogue_ids` on chat/plan. Returns
   * { categories: [{ id, name }] } sorted by name.
   */
  async getCategories() {
    try {
      const res = await aiAdvisorAPI.get('/ai-advisor/categories');
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // ---- Session (thread) management ----

  /** Create a new conversation thread. */
  async createSession(title) {
    try {
      const body = title ? { title } : {};
      const res = await aiAdvisorAPI.post('/ai-advisor/sessions', body);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  /** List my threads (newest-used first). */
  async listSessions({ limit = 50, offset = 0 } = {}) {
    try {
      const query = new URLSearchParams({ limit, offset });
      const res = await aiAdvisorAPI.get(`/ai-advisor/sessions?${query.toString()}`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  /** Get a thread plus its messages. */
  async getSession(id) {
    try {
      const res = await aiAdvisorAPI.get(`/ai-advisor/sessions/${id}`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  /** Rename a thread. */
  async renameSession(id, title) {
    try {
      const res = await aiAdvisorAPI.patch(`/ai-advisor/sessions/${id}`, { title });
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  /** Delete a thread. */
  async deleteSession(id) {
    try {
      const res = await aiAdvisorAPI.delete(`/ai-advisor/sessions/${id}`);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  // ---- Chat & plan ----

  /**
   * Ask a question within a thread (multi-turn).
   * @param {number|string} id session id
   * @param {{ question: string, window_weeks?: number, warehouse_ids?: number[], catalogue_ids?: number[] }} params
   *   catalogue_ids = Sub Category filter; omit / [] analyses all sub categories.
   */
  async chat(id, { question, window_weeks, warehouse_ids, catalogue_ids } = {}) {
    try {
      const body = { question };
      if (window_weeks !== undefined && window_weeks !== null) body.window_weeks = window_weeks;
      if (Array.isArray(warehouse_ids) && warehouse_ids.length) body.warehouse_ids = warehouse_ids;
      if (Array.isArray(catalogue_ids) && catalogue_ids.length) body.catalogue_ids = catalogue_ids;
      const res = await aiAdvisorAPI.post(`/ai-advisor/sessions/${id}/chat`, body);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },

  /**
   * Generate a purchase plan within a thread.
   * @param {number|string} id session id
   * @param {{ window_weeks?: number, warehouse_ids?: number[], catalogue_ids?: number[] }} params
   *   catalogue_ids = Sub Category filter; omit / [] analyses all sub categories.
   */
  async plan(id, { window_weeks, warehouse_ids, catalogue_ids } = {}) {
    try {
      const body = {};
      if (window_weeks !== undefined && window_weeks !== null) body.window_weeks = window_weeks;
      if (Array.isArray(warehouse_ids) && warehouse_ids.length) body.warehouse_ids = warehouse_ids;
      if (Array.isArray(catalogue_ids) && catalogue_ids.length) body.catalogue_ids = catalogue_ids;
      const res = await aiAdvisorAPI.post(`/ai-advisor/sessions/${id}/plan`, body);
      return { success: true, data: res.data };
    } catch (error) {
      return fail(error);
    }
  },
};

export default aiAdvisorService;
